import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Video,
  Plus,
  Calendar,
  Clock,
  Users,
  Play,
  X,
  Radio,
  Search,
  Filter,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Info
} from 'lucide-react';
import { meetingsApi, coursesApi, SessionStats } from '../../lib/api';
import PremiumDropdown, { DropdownOption } from '../../components/ui/PremiumDropdown';
import DateTimePicker from '../../components/ui/DateTimePicker';
import toast from 'react-hot-toast';

interface Course {
  id: string;
  title: string;
  courseType: 'RECORDED' | 'LIVE';
  hasLiveMeetingAddon: boolean;
  thumbnailUrl?: string;
  totalSessions: number;
}

interface Meeting {
  id: string;
  title: string;
  description?: string;
  scheduledAt: string;
  duration: number;
  status: 'SCHEDULED' | 'LIVE' | 'ENDED' | 'CANCELLED';
  meetingType: 'SESSION' | 'QA_FINALE';
  sessionNumber?: number;
  jitsiRoomId: string;
  course: {
    id: string;
    title: string;
    courseType: 'RECORDED' | 'LIVE';
  };
}

interface CreateMeetingForm {
  courseId: string;
  title: string;
  description: string;
  scheduledAt: Date | null;
  duration: number;
  sessionNumber: number;
}

import { joinMeetingSecure } from '../../lib/jitsi';

export default function ProfessorMeetingsPage() {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [selectedCourseStats, setSelectedCourseStats] = useState<SessionStats | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  
  const [createForm, setCreateForm] = useState<CreateMeetingForm>({
    courseId: '',
    title: '',
    description: '',
    scheduledAt: null,
    duration: 60,
    sessionNumber: 1,
  });
  
  // Cancel meeting modal state
  const [cancelModal, setCancelModal] = useState<Meeting | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch session stats when course is selected
  const fetchCourseStats = async (courseId: string) => {
    setIsLoadingStats(true);
    try {
      const res = await meetingsApi.getSessionStats(courseId);
      if (res.data.success && res.data.data) {
        setSelectedCourseStats(res.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch session stats:', error);
      setSelectedCourseStats(null);
    } finally {
      setIsLoadingStats(false);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [meetingsRes, coursesRes] = await Promise.all([
        meetingsApi.getUpcoming({ limit: 50 }),
        coursesApi.getMyCourses(),
      ]);

      if (meetingsRes.data.success) {
        setMeetings(meetingsRes.data.data as Meeting[]);
      }

      if (coursesRes.data.success) {
        // Filter courses that can have meetings (LIVE courses or RECORDED with addon)
        const eligibleCourses = (coursesRes.data.data as Course[]).filter(
          c => c.courseType === 'LIVE' || c.hasLiveMeetingAddon
        );
        setCourses(eligibleCourses);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load meetings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateMeeting = async () => {
    if (!createForm.courseId || !createForm.title || !createForm.scheduledAt) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsCreating(true);
    try {
      const res = await meetingsApi.create({
        courseId: createForm.courseId,
        title: createForm.title,
        description: createForm.description || undefined,
        scheduledAt: createForm.scheduledAt!.toISOString(),
        duration: createForm.duration,
      });

      if (res.data.success) {
        toast.success('Meeting scheduled successfully!');
        setShowCreateModal(false);
        setCreateForm({
          courseId: '',
          title: '',
          description: '',
          scheduledAt: null,
          duration: 60,
          sessionNumber: 1,
        });
        // Refetch meetings list
        await fetchData();
      } else {
        console.error('Meeting creation failed:', res.data);
        toast.error(res.data.error || 'Failed to create meeting');
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to create meeting');
    } finally {
      setIsCreating(false);
    }
  };

  const handleStartMeeting = async (meeting: Meeting) => {
    try {
      await meetingsApi.start(meeting.id);
      toast.success('Meeting started!');
      
      // Join meeting securely with JWT (professor is moderator)
      try {
        const result = await joinMeetingSecure(meeting.id);
        console.log('Join meeting result:', result);
      } catch (error) {
        console.error('JWT join failed:', error);
        toast.error('Failed to join with secure token. Check console for details.');
      }
      
      fetchData();
    } catch (error) {
      console.error('Failed to start meeting:', error);
      toast.error('Failed to start meeting');
    }
  };

  const handleEndMeeting = async (meeting: Meeting) => {
    try {
      await meetingsApi.end(meeting.id);
      toast.success('Meeting ended');
      fetchData();
    } catch (error) {
      toast.error('Failed to end meeting');
    }
  };

  const handleCancelMeeting = async () => {
    if (!cancelModal) return;
    
    setIsCancelling(true);
    try {
      await meetingsApi.cancel(cancelModal.id);
      toast.success('Meeting cancelled');
      setCancelModal(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel meeting');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleJoinMeeting = async (meeting: Meeting) => {
    try {
      const result = await joinMeetingSecure(meeting.id);
      console.log('Join meeting result:', result);
    } catch (error) {
      console.error('JWT join failed:', error);
      toast.error('Failed to join with secure token. Check console for details.');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'LIVE':
        return {
          label: 'Live Now',
          className: 'bg-red-500/20 text-red-400 border-red-500/30',
          icon: Radio,
        };
      case 'SCHEDULED':
        return {
          label: 'Scheduled',
          className: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
          icon: Clock,
        };
      case 'ENDED':
        return {
          label: 'Ended',
          className: 'bg-white/10 text-white/50 border-white/20',
          icon: CheckCircle2,
        };
      case 'CANCELLED':
        return {
          label: 'Cancelled',
          className: 'bg-white/5 text-white/30 border-white/10',
          icon: X,
        };
      default:
        return {
          label: status,
          className: 'bg-white/10 text-white/50 border-white/20',
          icon: Clock,
        };
    }
  };

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      }),
      time: date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      }),
    };
  };

  const filteredMeetings = meetings.filter(meeting => {
    const matchesStatus = filterStatus === 'all' || meeting.status === filterStatus;
    const matchesSearch = meeting.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         meeting.course.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const upcomingMeetings = filteredMeetings.filter(m => 
    m.status === 'SCHEDULED' || m.status === 'LIVE'
  );
  const pastMeetings = filteredMeetings.filter(m => 
    m.status === 'ENDED' || m.status === 'CANCELLED'
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-12 w-64 rounded-xl" />
        <div className="skeleton h-32 rounded-2xl" />
        <div className="grid gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton h-24 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8"
      >
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
            <Video className="w-7 h-7 text-purple-400" />
          </div>
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-white">Live Sessions</h1>
            <p className="text-white/50">Schedule and manage your live meetings</p>
          </div>
        </div>

        <button
          onClick={() => setShowCreateModal(true)}
          disabled={courses.length === 0}
          className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-5 h-5" />
          Schedule Meeting
        </button>
      </motion.div>

      {/* No eligible courses notice */}
      {courses.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-6 rounded-2xl bg-amber-500/10 border border-amber-500/20 mb-8"
        >
          <div className="flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-amber-400 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-amber-200 mb-1">No eligible courses</h3>
              <p className="text-amber-200/70 text-sm">
                To schedule live sessions, you need either a Live Session course or a 
                Video-Based course with the Live Q&A addon enabled. 
                <a href="/professor/courses/new" className="text-amber-400 hover:underline ml-1">
                  Create a new course
                </a>
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex flex-col sm:flex-row gap-4 mb-6"
      >
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search meetings..."
            className="input pl-12 w-full"
          />
        </div>

        <PremiumDropdown
          label="Status"
          icon={<Filter className="w-4 h-4" />}
          options={[
            { value: 'all', label: 'All Status' },
            { value: 'SCHEDULED', label: 'Scheduled' },
            { value: 'LIVE', label: 'Live' },
            { value: 'ENDED', label: 'Ended' },
            { value: 'CANCELLED', label: 'Cancelled' },
          ] as DropdownOption[]}
          value={filterStatus}
          onChange={setFilterStatus}
          placeholder="All Status"
        />
      </motion.div>

      {/* Upcoming Meetings */}
      {upcomingMeetings.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-400" />
            Upcoming Sessions ({upcomingMeetings.length})
          </h2>

          <div className="space-y-4">
            {upcomingMeetings.map((meeting, index) => {
              const status = getStatusBadge(meeting.status);
              const { date, time } = formatDateTime(meeting.scheduledAt);
              const isLive = meeting.status === 'LIVE';

              return (
                <motion.div
                  key={meeting.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + index * 0.05 }}
                  className={`p-5 rounded-xl transition-all ${
                    isLive 
                      ? 'bg-gradient-to-r from-red-500/10 to-purple-500/10 border-2 border-red-500/30' 
                      : 'bg-white/[0.02] border border-white/10 hover:border-white/20'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    {/* Meeting Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-white truncate">{meeting.title}</h3>
                        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${status.className}`}>
                          <status.icon className="w-3 h-3" />
                          {status.label}
                        </div>
                        {meeting.meetingType === 'QA_FINALE' && (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-purple-500/20 text-purple-300">
                            Q&A Session
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-white/50">
                        <span className="flex items-center gap-1.5">
                          <Calendar className="w-4 h-4" />
                          {date}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-4 h-4" />
                          {time} ({meeting.duration} min)
                        </span>
                        <span className="flex items-center gap-1.5 text-white/40">
                          <Video className="w-4 h-4" />
                          {meeting.course.title}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {isLive ? (
                        <>
                          <button
                            onClick={() => handleJoinMeeting(meeting)}
                            className="px-4 py-2 rounded-lg bg-red-500 text-white font-medium flex items-center gap-2 hover:bg-red-600 transition-colors animate-pulse"
                          >
                            <Radio className="w-4 h-4" />
                            Join Live
                          </button>
                          <button
                            onClick={() => handleEndMeeting(meeting)}
                            className="px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors"
                          >
                            End
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleStartMeeting(meeting)}
                            className="px-4 py-2 rounded-lg bg-emerald-500/20 text-emerald-400 font-medium flex items-center gap-2 hover:bg-emerald-500/30 transition-colors"
                          >
                            <Play className="w-4 h-4" />
                            Start
                          </button>
                          <button
                            onClick={() => setCancelModal(meeting)}
                            className="p-2 rounded-lg text-white/40 hover:text-coral hover:bg-coral/10 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Past Meetings */}
      {pastMeetings.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-white/40" />
            Past Sessions ({pastMeetings.length})
          </h2>

          <div className="space-y-3">
            {pastMeetings.map((meeting, index) => {
              const status = getStatusBadge(meeting.status);
              const { date, time } = formatDateTime(meeting.scheduledAt);

              return (
                <motion.div
                  key={meeting.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + index * 0.03 }}
                  className="p-4 rounded-xl bg-white/[0.02] border border-white/5"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-medium text-white/70 truncate">{meeting.title}</h3>
                        <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${status.className}`}>
                          {status.label}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-white/40">
                        <span>{date} at {time}</span>
                        <span>{meeting.course.title}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Empty State */}
      {meetings.length === 0 && courses.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-16"
        >
          <div className="w-20 h-20 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-6">
            <Video className="w-10 h-10 text-white/30" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">No meetings scheduled</h3>
          <p className="text-white/50 mb-6 max-w-md mx-auto">
            Schedule your first live session to engage with your students in real-time.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Schedule Your First Meeting
          </button>
        </motion.div>
      )}

      {/* Cancel Meeting Confirmation Modal */}
      <AnimatePresence>
        {cancelModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => !isCancelling && setCancelModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-2xl p-6"
              style={{
                background: 'linear-gradient(180deg, #18181f 0%, #111116 100%)',
                border: '1px solid rgba(255,255,255,0.08)',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
              }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-rose-500/15 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-rose-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Cancel Meeting</h3>
                  <p className="text-sm text-white/40">This action cannot be undone</p>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.04] mb-4">
                <p className="text-white/70 text-sm mb-2">
                  Are you sure you want to cancel this meeting?
                </p>
                <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.04]">
                  <p className="font-medium text-white">{cancelModal.title}</p>
                  <p className="text-sm text-white/50 mt-1">
                    {new Date(cancelModal.scheduledAt).toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                    })} at {new Date(cancelModal.scheduledAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 mb-6">
                <Info className="w-4 h-4 text-amber-400 flex-shrink-0" />
                <p className="text-amber-400 text-sm">
                  Enrolled students will be notified about the cancellation.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setCancelModal(null)}
                  disabled={isCancelling}
                  className="flex-1 px-4 py-3 rounded-xl font-medium text-white/70 
                           bg-white/[0.04] border border-white/[0.06]
                           hover:bg-white/[0.08] transition-colors
                           disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Keep Meeting
                </button>
                <button
                  onClick={handleCancelMeeting}
                  disabled={isCancelling}
                  className="flex-1 px-4 py-3 rounded-xl font-medium text-white 
                           bg-rose-500/80 hover:bg-rose-500 
                           disabled:opacity-50 disabled:cursor-not-allowed
                           transition-colors flex items-center justify-center gap-2"
                >
                  {isCancelling ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Cancelling...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Cancel Meeting
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Meeting Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
              style={{
                background: 'linear-gradient(145deg, rgba(30, 30, 35, 0.98) 0%, rgba(15, 16, 20, 0.98) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Schedule Live Session</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-5">
                {/* Course Selection */}
                <div>
                  <label className="label">Select Course *</label>
                  <select
                    value={createForm.courseId}
                    onChange={(e) => {
                      const course = courses.find(c => c.id === e.target.value);
                      setSelectedCourse(course || null);
                      setSelectedCourseStats(null);
                      if (course) {
                        fetchCourseStats(course.id);
                      }
                      setCreateForm(prev => ({ 
                        ...prev, 
                        courseId: e.target.value,
                        title: course?.courseType === 'RECORDED' 
                          ? `Q&A Session - ${course.title}`
                          : ''
                      }));
                    }}
                    className="input w-full"
                  >
                    <option value="">Choose a course</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.title} ({course.courseType === 'LIVE' ? `Live - ${course.totalSessions} sessions` : 'Q&A Session'})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Session Stats - Show remaining slots and enrollment info */}
                {selectedCourse && (
                  <div className={`p-4 rounded-xl border ${
                    isLoadingStats 
                      ? 'bg-white/5 border-white/10'
                      : selectedCourseStats?.canScheduleMore 
                        ? 'bg-emerald-500/10 border-emerald-500/20'
                        : 'bg-red-500/10 border-red-500/20'
                  }`}>
                    {isLoadingStats ? (
                      <div className="flex items-center gap-2 text-white/50">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Loading session info...
                      </div>
                    ) : selectedCourseStats ? (
                      <div>
                        {/* Enrollment Status */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-white/40" />
                            <span className="text-sm text-white/60">Enrolled Students</span>
                          </div>
                          <span className={`text-sm font-bold ${
                            selectedCourseStats.hasEnrollments ? 'text-emerald-400' : 'text-red-400'
                          }`}>
                            {selectedCourseStats.enrolledStudents}
                          </span>
                        </div>

                        {/* No enrollments warning */}
                        {!selectedCourseStats.hasEnrollments && (
                          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 text-sm text-red-300 mb-3">
                            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span>
                              Cannot schedule meetings until students enroll in this course.
                            </span>
                          </div>
                        )}

                        {/* Session Slots - only show if there are enrollments */}
                        {selectedCourseStats.hasEnrollments && (
                          <>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-white">Session Slots</span>
                              {selectedCourse.courseType === 'LIVE' && (
                                <span className={`text-sm font-bold ${
                                  selectedCourseStats.remainingSlots > 0 ? 'text-emerald-400' : 'text-red-400'
                                }`}>
                                  {selectedCourseStats.remainingSlots} / {selectedCourseStats.totalSessions} remaining
                                </span>
                              )}
                            </div>
                            
                            {selectedCourse.courseType === 'LIVE' && (
                              <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-emerald-500 rounded-full transition-all"
                                  style={{ 
                                    width: `${(selectedCourseStats.scheduledSessions / selectedCourseStats.totalSessions) * 100}%` 
                                  }}
                                />
                              </div>
                            )}
                            
                            {selectedCourseStats.remainingSlots === 0 && (
                              <div className="flex items-start gap-2 mt-3 text-sm text-red-300">
                                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                <span>
                                  {selectedCourse.courseType === 'LIVE' 
                                    ? 'All session slots are used. To add more sessions, create a new course.'
                                    : 'A Q&A session is already scheduled for this course.'}
                                </span>
                              </div>
                            )}
                            
                            {selectedCourseStats.canScheduleMore && selectedCourse.courseType === 'LIVE' && (
                              <p className="text-xs text-white/40 mt-2">
                                This will be Session #{selectedCourseStats.scheduledSessions + 1}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}

                {/* Meeting Title */}
                <div>
                  <label className="label">Meeting Title *</label>
                  <input
                    type="text"
                    value={createForm.title}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder={selectedCourse?.courseType === 'LIVE' 
                      ? "e.g., Session 1: Introduction" 
                      : "e.g., Q&A Session with Students"}
                    className="input w-full"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="label">Description (optional)</label>
                  <textarea
                    value={createForm.description}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="What will be covered in this session?"
                    rows={3}
                    className="input w-full resize-none"
                  />
                </div>

                {/* Date & Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Date & Time *</label>
                    <DateTimePicker
                      selected={createForm.scheduledAt}
                      onChange={(date) => setCreateForm(prev => ({ ...prev, scheduledAt: date }))}
                      minDate={new Date()}
                      placeholderText="Select date & time"
                    />
                  </div>

                  <div>
                    <label className="label">Duration (minutes)</label>
                    <select
                      value={createForm.duration}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                      className="input w-full h-[52px]"
                    >
                      <option value={30}>30 minutes</option>
                      <option value={45}>45 minutes</option>
                      <option value={60}>1 hour</option>
                      <option value={90}>1.5 hours</option>
                      <option value={120}>2 hours</option>
                      <option value={180}>3 hours</option>
                    </select>
                  </div>
                </div>

                {/* Info */}
                <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <p className="text-sm text-blue-200/80">
                    Students enrolled in this course will automatically receive a notification 
                    about this scheduled session.
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      setSelectedCourse(null);
                      setSelectedCourseStats(null);
                    }}
                    className="px-5 py-2.5 rounded-xl text-white/60 hover:text-white hover:bg-white/5 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateMeeting}
                    disabled={
                      isCreating || 
                      !createForm.courseId || 
                      !createForm.title || 
                      !createForm.scheduledAt ||
                      isLoadingStats ||
                      !!(selectedCourseStats && !selectedCourseStats.canScheduleMore)
                    }
                    className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isCreating ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Calendar className="w-4 h-4" />
                        Schedule Meeting
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
